import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { WELLS_API_URL } from '../../constants';
import type { RegionFieldDto, RegionMasterDto, WellMaster } from '../../domain/types';
import { mapApiWell } from '../../domain/wells';

const WELL_KEY_ALPHANUMERIC = /^[A-Za-z0-9]+$/;

interface AddWellModalProps {
  open: boolean;
  onClose: () => void;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  activeRegions: RegionMasterDto[];
  onWellCreated: (well: WellMaster) => void | Promise<void>;
}

export function AddWellModal({
  open,
  onClose,
  authFetch,
  activeRegions,
  onWellCreated
}: AddWellModalProps): ReactElement | null {
  const [wellKey, setWellKey] = useState('');
  const [wellName, setWellName] = useState('');
  const [region, setRegion] = useState('');
  const [fieldId, setFieldId] = useState('');
  const [customFieldName, setCustomFieldName] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [depthFt, setDepthFt] = useState('');
  const [reservoirType, setReservoirType] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const regionOptions = activeRegions.filter((r) => r.isActive);

  const fieldOptions: RegionFieldDto[] = useMemo(() => {
    const row = activeRegions.find((r) => r.code === region);
    return row?.fields ?? [];
  }, [activeRegions, region]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setWellKey('');
    setWellName('');
    setRegion('');
    setFieldId('');
    setCustomFieldName('');
    setState('');
    setCountry('');
    setDepthFt('');
    setReservoirType('');
    setError('');
    setSaving(false);
  }, [open]);

  async function submit(): Promise<void> {
    setError('');
    const key = wellKey.trim().toUpperCase();
    if (!key || !WELL_KEY_ALPHANUMERIC.test(wellKey.trim())) {
      setError('Well id is required and must be alphanumeric only.');
      return;
    }
    if (!wellName.trim()) {
      setError('Well name is required.');
      return;
    }
    if (!region) {
      setError('Select a region.');
      return;
    }
    const depthNum = depthFt.trim() === '' ? null : Number(depthFt);
    if (depthFt.trim() !== '' && Number.isNaN(depthNum)) {
      setError('Depth must be a number.');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(WELLS_API_URL, {
        method: 'POST',
        body: JSON.stringify({
          wellKey: key,
          wellName: wellName.trim(),
          field: fieldId.trim() ? '' : customFieldName.trim(),
          fieldId: fieldId.trim() || null,
          fieldCounty: fieldId.trim() ? null : '',
          fieldState: fieldId.trim() ? null : '',
          region,
          state: state.trim(),
          country: country.trim(),
          depthFt: depthNum,
          reservoirType: reservoirType.trim() || null
        })
      });
      if (response.status === 409) {
        const body = (await response.json()) as { message?: string };
        setError(body.message ?? 'A well with this id already exists.');
        return;
      }
      if (!response.ok) {
        const text = await response.text();
        setError(text || 'Could not create well.');
        return;
      }
      const raw = (await response.json()) as Record<string, unknown>;
      const created = mapApiWell(raw);
      await onWellCreated(created);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop modal-backdrop--centered" role="presentation" onClick={onClose}>
      <div
        className="modal-dialog modal-dialog-sm add-well-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-well-title"
        aria-describedby="add-well-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="add-well-modal__header">
          <div>
            <h2 id="add-well-title">Add well</h2>
            <p id="add-well-desc" className="add-well-modal__subtitle">
              Add a well to the directory so requestors can tie procurement requests to a valid location.
            </p>
          </div>
          <button type="button" className="secondary icon-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="add-well-modal__body grid">
          <section className="add-well-modal__section" aria-labelledby="add-well-sec-identity">
            <h3 className="form-section-title" id="add-well-sec-identity">
              Identity
            </h3>
            <label className="full-width">
              Well id
              <input
                value={wellKey}
                onChange={(e) => setWellKey(e.target.value.toUpperCase())}
                placeholder="Alphanumeric, e.g. ACME01"
                autoComplete="off"
              />
            </label>
            <label className="full-width">
              Well name
              <input
                value={wellName}
                onChange={(e) => setWellName(e.target.value)}
                placeholder="Display name"
                autoComplete="off"
              />
            </label>
          </section>

          <section className="add-well-modal__section" aria-labelledby="add-well-sec-region">
            <h3 className="form-section-title" id="add-well-sec-region">
              Region &amp; field
            </h3>
            <label className="full-width">
              Region
              <select
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setFieldId('');
                }}
              >
                <option value="">Select region…</option>
                {regionOptions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="full-width">
              Field (region catalog)
              <select
                value={fieldId}
                disabled={!region || fieldOptions.length === 0}
                onChange={(e) => setFieldId(e.target.value)}
              >
                <option value="">
                  {!region
                    ? 'Select a region…'
                    : fieldOptions.length === 0
                      ? 'No fields configured for this region'
                      : 'Select field…'}
                </option>
                {fieldOptions.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} — {f.county}, {f.state}
                  </option>
                ))}
              </select>
            </label>
            {!fieldId.trim() ? (
              <label className="full-width">
                Field name (custom)
                <input
                  value={customFieldName}
                  onChange={(e) => setCustomFieldName(e.target.value)}
                  placeholder="If not using catalog"
                  autoComplete="off"
                />
              </label>
            ) : null}
          </section>

          <section className="add-well-modal__section" aria-labelledby="add-well-sec-location">
            <h3 className="form-section-title" id="add-well-sec-location">
              Location
            </h3>
            <div className="row full-width add-well-modal__row-two">
              <label>
                State
                <input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. TX" autoComplete="off" />
              </label>
              <label>
                Country
                <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. USA" autoComplete="off" />
              </label>
            </div>
          </section>

          <section className="add-well-modal__section" aria-labelledby="add-well-sec-tech">
            <h3 className="form-section-title" id="add-well-sec-tech">
              Technical
            </h3>
            <label className="full-width">
              Depth (ft)
              <input
                type="text"
                inputMode="decimal"
                value={depthFt}
                onChange={(e) => setDepthFt(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
              />
            </label>
            <label className="full-width">
              Reservoir type
              <input
                value={reservoirType}
                onChange={(e) => setReservoirType(e.target.value)}
                placeholder="e.g. Sandstone"
                autoComplete="off"
              />
            </label>
          </section>
        </div>

        <footer className="add-well-modal__footer">
          {error ? (
            <p className="error add-well-modal__error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="add-well-modal__actions">
            <button type="button" className="secondary" disabled={saving} onClick={onClose}>
              Cancel
            </button>
            <button type="button" disabled={saving} onClick={() => void submit()}>
              {saving ? 'Saving…' : 'Create well'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
